import React,{useRef,useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PostArtwork from './PostArtwork';
import DownloadPostButton from './DownloadPostButton';
import PlatformBadges from './PlatformBadges';
import PublishModal from './PublishModal';
import {formatMetric} from '../data/mockEngagement';

const {
  FiArrowUpRight,
  FiHeart,
  FiMessageCircle,
  FiPlus,
  FiSend
}=FiIcons;

function LibraryPostCard({
  post,
  brand,
  index,
  onOpen,
  setPublishingPost
}) {
  const artworkRef=useRef(null);

  return (
    <motion.article
      className="post-card"
      initial={{opacity:0,y:14}}
      animate={{opacity:1,y:0}}
      transition={{delay:index*0.06}}
    >
      <button
        className="thumbnail-button"
        onClick={()=> onOpen(post)}
      >
        <PostArtwork ref={artworkRef} post={post} brand={brand} compact />
      </button>

      <div className="post-card-info">
        <div className="status-row">
          <span className={`status ${post.status}`}>
            {post.status}
          </span>
          <span className="post-status-meta">
            <PlatformBadges
              platforms={post.publishedPlatforms}
            />
            <time>{post.createdAt}</time>
          </span>
        </div>

        <h3>{post.headline}</h3>
        <p>{post.prompt}</p>

        {post.status==='posted' && (
          <EngagementLine engagement={post.engagement} />
        )}

        <div className="card-actions">
          <button onClick={()=> onOpen(post)}>
            Open
            <SafeIcon icon={FiArrowUpRight} />
          </button>

          <DownloadPostButton
            artworkRef={artworkRef}
            headline={post.headline}
            className=""
            showError={false}
          />

          {post.status==='approved' && (
            <button
              className="publish-post-button"
              onClick={()=> setPublishingPost(post)}
            >
              <SafeIcon icon={FiSend} />
              Publish
            </button>
          )}
        </div>
      </div>
    </motion.article>
  );
}

function EngagementLine({engagement}) {
  if (!engagement) {
    return null;
  }

  return (
    <div
      className="post-engagement-line"
      aria-label={`${engagement.likes} likes and ${engagement.comments} comments`}
    >
      <span>
        <SafeIcon icon={FiHeart} />
        {formatMetric(engagement.likes)} likes
      </span>
      <i aria-hidden="true" />
      <span>
        <SafeIcon icon={FiMessageCircle} />
        {formatMetric(engagement.comments)} comments
      </span>
    </div>
  );
}

function LibraryScreen({
  posts,
  brand,
  loading,
  error,
  connections,
  onCreate,
  onOpen,
  onPublish,
  onOpenConnections
}) {
  const [publishingPost,setPublishingPost]=useState(null);

  return (
    <main className="page library-page">
      <header className="library-header">
        <div>
          <span className="eyebrow">{brand.name}</span>
          <h1>Post library</h1>
          <p>Every idea, draft, and published post in one place.</p>
        </div>
        <button
          className="primary-button"
          style={{background:brand.primary}}
          onClick={onCreate}
        >
          <SafeIcon icon={FiPlus} />
          Create new
        </button>
      </header>

      <div className="library-toolbar">
        <span>{posts.length} posts</span>
        <div>
          <button className="active">All posts</button>
          <button>Recent</button>
        </div>
      </div>

      {error && (
        <p className="library-error" role="alert">{error}</p>
      )}

      {loading ? (
        <div className="empty-state library-loading">
          <p>Loading posts…</p>
        </div>
      ) : posts.length ? (
        <div className="post-grid">
          {posts.map((post,index)=> (
            <LibraryPostCard
              key={post.id}
              post={post}
              brand={brand}
              index={index}
              onOpen={onOpen}
              setPublishingPost={setPublishingPost}
            />
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <h2>Your next great post starts here.</h2>
          <p>Create the first post for {brand.name}.</p>
          <button className="primary-button" onClick={onCreate}>
            Create a post
          </button>
        </div>
      )}

      {publishingPost && (
        <PublishModal
          post={publishingPost}
          brand={brand}
          connections={connections}
          onClose={()=> setPublishingPost(null)}
          onConnect={onOpenConnections}
          onPublished={onPublish}
        />
      )}
    </main>
  );
}

export default LibraryScreen;